import { STUDIO_DEMO_URL } from 'src/constants/externalUrls';
import { useFeatureFlags } from 'src/context';
import { useAppBuilderAccess } from 'src/hooks/useAppBuilderAccess';
import { useAppSelector } from 'src/hooks/useStore';
import {
  useGetOnboardingTaskStatusQuery,
  useMarkOnboardingSurfaceSeenMutation,
} from 'src/services/api/improvedOnboardingApi';

import {
  areActivationStepsCompleted,
  getActivationStepCompletion,
  hasActivationTasks,
} from './activationSteps';
import { MeetAssemblyModal } from './MeetAssemblyModal';

// Every book-a-call CTA points at the sales-assisted booking page, and there's no
// Slack Connect link yet, so both Meet Assembly options open it for now.
const KICKOFF_CALL_URL = STUDIO_DEMO_URL;
const SLACK_CHANNEL_URL = STUDIO_DEMO_URL;

/**
 * The Meet Assembly offer, shown once both activation checklists are done, for
 * business emails only. It lives outside the sidebar because the mobile drawer
 * renders the sidebar twice, which would open the offer twice.
 */
export const MeetAssemblyOffer = () => {
  const { EnableNewSidebar, EnableStudioFTUX } = useFeatureFlags();
  const isClient = useAppSelector((state) => state.user.isClient);
  // The same gates that show the Getting started card in the sidebar.
  const { hasAppBuilderPlanAccess } = useAppBuilderAccess();
  const isEligible =
    !isClient &&
    EnableNewSidebar &&
    EnableStudioFTUX &&
    hasAppBuilderPlanAccess;
  const { data: onboardingStatus } = useGetOnboardingTaskStatusQuery(
    {},
    { skip: !isEligible },
  );
  const [markSurfaceSeen] = useMarkOnboardingSurfaceSeenMutation();

  const tasks = onboardingStatus?.additionalFields.tasks;
  // Recorded on the way out, so the offer waits for the user to act on it and
  // still shows on a later load if they never did.
  const isOpen =
    isEligible &&
    hasActivationTasks(tasks) &&
    areActivationStepsCompleted(getActivationStepCompletion(tasks)) &&
    Boolean(onboardingStatus?.additionalFields.isBusinessEmail) &&
    !onboardingStatus?.fields.seenAt?.meetAssembly;

  const close = () => {
    void markSurfaceSeen('meetAssembly');
  };

  const openAndClose = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    close();
  };

  return (
    <MeetAssemblyModal
      isOpen={isOpen}
      onClose={close}
      onScheduleKickoffCall={() => openAndClose(KICKOFF_CALL_URL)}
      onConnectSlackChannel={() => openAndClose(SLACK_CHANNEL_URL)}
    />
  );
};
